import type { StaffSponsoredStreamWire } from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { Field, FieldDescription, FieldError, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Ellipsis, ExternalLink, Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { ShortDate } from "../components/ShortDate";
import { formatDate } from "../lib/format";
import { parseShowHours, parseStreamPriority } from "../lib/liveBreakStaff";
import { allowerLabel } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";
import { useStandingNotice } from "../lib/useStandingNotice";
import { useTenantRole } from "../lib/useTenantRole";

type Stream = StaffSponsoredStreamWire;

const streamColumns = createDataTableColumns<Stream>();
const PINNED = { start: [], end: ["actions"] };

function buildColumns(canEdit: boolean) {
  const columns = [
    streamColumns.accessor("priority", {
      header: "Priority",
      size: 96,
      meta: { align: "end", filter: "number" },
    }),
    streamColumns.accessor("twitchLogin", {
      header: "Twitch login",
      cell: ({ getValue, row }) => (
        <a
          href={row.original.twitchUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-mono text-xs hover:underline"
        >
          {getValue()}
          <ExternalLink className="size-3" aria-hidden />
        </a>
      ),
      meta: { filter: "text" },
    }),
    streamColumns.accessor((stream) => stream.displayName ?? "", {
      id: "displayName",
      header: "Display name",
      cell: ({ getValue }) =>
        getValue() || <span className="text-muted-foreground">Twitch's own</span>,
      meta: { filter: "text" },
    }),
    streamColumns.accessor((stream) => (stream.isShown ? "Shown" : "Hidden"), {
      id: "shown",
      header: "Widget",
      cell: ({ getValue, row }) =>
        row.original.isShown ? (
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{getValue()}</Badge>
            {row.original.shownUntil && (
              <span className="text-xs text-muted-foreground">
                until <ShortDate value={row.original.shownUntil} />
              </span>
            )}
          </span>
        ) : (
          <Badge variant="outline">{row.original.shownUntil ? "Expired" : getValue()}</Badge>
        ),
      meta: { filter: "text" },
    }),
    streamColumns.accessor((stream) => allowerLabel(stream.updatedBy), {
      id: "updatedBy",
      header: "Last changed by",
      cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
      meta: { filter: "text" },
    }),
    streamColumns.accessor("updatedAt", {
      header: "Updated",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ];
  if (!canEdit) return streamColumns.columns(columns);
  return streamColumns.columns([
    ...columns,
    streamColumns.display({
      id: "actions",
      size: 56,
      cell: ({ row }) => <StreamActions stream={row.original} />,
    }),
  ]);
}

/**
 * Sponsored streams (boxed's sponsored-stream tools): Twitch streamers the storefront's live-break
 * widget shows, lowest priority first, for 1–72 hours at a time. A display name also names the
 * channel in a live break's note about Priority. Any member reads; editors add, edit, show, hide
 * and delete.
 */
export function LiveBreakSponsoredStreamsPage() {
  const { tenantId } = useParams({
    from: "/app/tenants/$tenantId/storefront/live-breaks/sponsored-streams",
  });
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const streams = useQuery(trpc.liveBreaks.sponsoredStreams.list.queryOptions());
  const twitch = useQuery(trpc.twitchApps.get.queryOptions());
  const rows = useMemo(() => streams.data?.streams ?? [], [streams.data]);
  const columns = useMemo(() => buildColumns(canEdit), [canEdit]);

  // The widget reads nothing without the environment's own Twitch app.
  useStandingNotice(twitch.isSuccess && twitch.data.app === null, {
    id: "sponsored-streams-no-twitch-app",
    message:
      "This environment has no Twitch app, so the storefront shows no sponsored streams until one is saved.",
    action: can("admin")
      ? {
          label: "Set up",
          onClick: () =>
            void navigate({
              to: "/tenants/$tenantId/storefront/live-breaks",
              params: { tenantId },
            }),
        }
      : undefined,
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Sponsored streams</h1>
          <p className="text-sm text-muted-foreground">
            Twitch streamers the storefront's live-break widget shows, lowest priority first, for 1
            to 72 hours at a time. A display name also names the channel in a live break's note
            about Priority.
          </p>
        </div>
        {canEdit && <AddStreamDialog />}
      </div>

      {streams.error ? (
        <ErrorAlert message={streams.error.message} />
      ) : (
        <Card>
          <CardContent>
            {streams.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(stream) => stream.id}
                pinned={canEdit ? PINNED : undefined}
                search="Search streams"
                csvName="sponsored-streams"
                emptyMessage={
                  canEdit
                    ? "No sponsored streams yet. Add a streamer by their Twitch login."
                    : "No sponsored streams yet."
                }
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Refreshes the list after a write. */
function useInvalidateStreams() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.sponsoredStreams.pathKey() });
}

/** The priority and display name fields both dialogs share, with what is wrong beneath. */
function StreamFields({
  idPrefix,
  priority,
  displayName,
  onPriority,
  onDisplayName,
}: {
  idPrefix: string;
  priority: string;
  displayName: string;
  onPriority: (value: string) => void;
  onDisplayName: (value: string) => void;
}) {
  const priorityValid = parseStreamPriority(priority) !== null;
  return (
    <>
      <Field data-invalid={!priorityValid || undefined}>
        <FieldLabel htmlFor={`${idPrefix}-priority`}>Priority</FieldLabel>
        <Input
          id={`${idPrefix}-priority`}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={priority}
          aria-invalid={!priorityValid}
          onChange={(e) => onPriority(e.target.value)}
        />
        {priorityValid ? (
          <FieldDescription>Lower shows first; a whole number from 1.</FieldDescription>
        ) : (
          <FieldError>A whole number from 1.</FieldError>
        )}
      </Field>
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-display-name`}>Display name</FieldLabel>
        <Input
          id={`${idPrefix}-display-name`}
          value={displayName}
          maxLength={80}
          onChange={(e) => onDisplayName(e.target.value)}
        />
        <FieldDescription>Optional. Blank shows the channel's own Twitch name.</FieldDescription>
      </Field>
    </>
  );
}

/** Lists a streamer, hidden until shown (`liveBreaks.sponsoredStreams.create`, editors). */
function AddStreamDialog() {
  const trpc = useTRPC();
  const invalidate = useInvalidateStreams();
  const [open, setOpen] = useState(false);
  const [login, setLogin] = useState("");
  const [priority, setPriority] = useState("1");
  const [displayName, setDisplayName] = useState("");
  const reset = () => {
    setLogin("");
    setPriority("1");
    setDisplayName("");
  };
  const create = useMutation(
    trpc.liveBreaks.sponsoredStreams.create.mutationOptions({
      onSuccess: ({ stream }) => {
        toast.success(`${stream.twitchLogin} is listed. Show it to put it in the widget.`);
        invalidate();
        setOpen(false);
        reset();
      },
    }),
  );
  const parsedPriority = parseStreamPriority(priority);
  const ready = login.trim() !== "" && parsedPriority !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger render={<Button type="button" size="sm" />}>
        <Plus />
        Add stream
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || parsedPriority === null) return;
            create.mutate({
              twitchLogin: login.trim(),
              priority: parsedPriority,
              displayName: displayName.trim() || null,
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>Add a sponsored stream</DialogTitle>
            <DialogDescription>
              It starts hidden: show it for a number of hours to put it in the widget.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="stream-login">Twitch login</FieldLabel>
            <Input
              id="stream-login"
              value={login}
              autoComplete="off"
              placeholder="e.g. boxedgg"
              maxLength={25}
              onChange={(e) => setLogin(e.target.value)}
            />
            <FieldDescription>The channel's login: letters, digits or _.</FieldDescription>
          </Field>
          <StreamFields
            idPrefix="stream-new"
            priority={priority}
            displayName={displayName}
            onPriority={setPriority}
            onDisplayName={setDisplayName}
          />
          <DialogFooter>
            <Button type="submit" disabled={!ready || create.isPending}>
              {create.isPending ? "Adding…" : "Add stream"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Which of a row's dialogs is open. */
type StreamDialog = "show" | "edit" | "delete";

/**
 * A row's writes behind one "More actions" menu, so the pinned column stays narrow at a phone's
 * width: Show (Extend while shown) for a number of hours, Hide, Edit, Delete. Each dialog mounts
 * when its item is chosen, so it opens on the row's current values.
 */
function StreamActions({ stream }: { stream: Stream }) {
  const trpc = useTRPC();
  const invalidate = useInvalidateStreams();
  const [dialog, setDialog] = useState<StreamDialog | null>(null);
  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const hide = useMutation(
    trpc.liveBreaks.sponsoredStreams.hide.mutationOptions({
      onSuccess: () => {
        toast.success(`${stream.twitchLogin} is hidden.`);
        invalidate();
      },
    }),
  );

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`More actions for ${stream.twitchLogin}`}
            />
          }
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          <DropdownMenuItem onClick={() => setDialog("show")}>
            <Eye />
            {stream.isShown ? "Extend…" : "Show…"}
          </DropdownMenuItem>
          {stream.isShown && (
            <DropdownMenuItem
              disabled={hide.isPending}
              onClick={() => hide.mutate({ id: stream.id })}
            >
              <EyeOff />
              Hide
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setDialog("edit")}>
            <Pencil />
            Edit…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => setDialog("delete")}>
            <Trash2 />
            Delete…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog === "show" && <ShowStreamDialog stream={stream} onOpenChange={close} />}
      {dialog === "edit" && <EditStreamDialog stream={stream} onOpenChange={close} />}
      {dialog === "delete" && <DeleteStreamDialog stream={stream} onOpenChange={close} />}
    </div>
  );
}

/** Shows a stream for 1–72 hours from now, replacing any current end (`.show`). */
function ShowStreamDialog({
  stream,
  onOpenChange,
}: {
  stream: Stream;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const invalidate = useInvalidateStreams();
  const [hours, setHours] = useState("24");
  const show = useMutation(
    trpc.liveBreaks.sponsoredStreams.show.mutationOptions({
      onSuccess: ({ stream: shown }) => {
        toast.success(`${shown.twitchLogin} shows until ${formatDate(shown.shownUntil)}.`);
        invalidate();
        onOpenChange(false);
      },
    }),
  );
  const parsed = parseShowHours(hours);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (parsed !== null) show.mutate({ id: stream.id, hours: parsed });
          }}
        >
          <DialogHeader>
            <DialogTitle>{`Show ${stream.twitchLogin}`}</DialogTitle>
            <DialogDescription>
              The widget shows it from now for this many hours. A new length replaces the current
              one rather than adding to it.
            </DialogDescription>
          </DialogHeader>
          <Field data-invalid={parsed === null || undefined}>
            <FieldLabel htmlFor={`stream-hours-${stream.id}`}>Hours</FieldLabel>
            <Input
              id={`stream-hours-${stream.id}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={72}
              step={1}
              value={hours}
              aria-invalid={parsed === null}
              onChange={(e) => setHours(e.target.value)}
            />
            {parsed === null ? (
              <FieldError>A whole number from 1 to 72.</FieldError>
            ) : (
              <FieldDescription>1 to 72.</FieldDescription>
            )}
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={parsed === null || show.isPending}>
              {show.isPending ? "Showing…" : "Show"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Changes a stream's priority and display name (`.update`). */
function EditStreamDialog({
  stream,
  onOpenChange,
}: {
  stream: Stream;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const invalidate = useInvalidateStreams();
  const [priority, setPriority] = useState(String(stream.priority));
  const [displayName, setDisplayName] = useState(stream.displayName ?? "");
  const update = useMutation(
    trpc.liveBreaks.sponsoredStreams.update.mutationOptions({
      onSuccess: () => {
        toast.success(`${stream.twitchLogin} saved.`);
        invalidate();
        onOpenChange(false);
      },
    }),
  );
  const parsedPriority = parseStreamPriority(priority);
  const changed =
    parsedPriority !== stream.priority || displayName.trim() !== (stream.displayName ?? "");

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (parsedPriority === null) return;
            update.mutate({
              id: stream.id,
              priority: parsedPriority,
              displayName: displayName.trim(),
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>{`Edit ${stream.twitchLogin}`}</DialogTitle>
            <DialogDescription>
              The login stays; delete and add again to change it.
            </DialogDescription>
          </DialogHeader>
          <StreamFields
            idPrefix={`stream-${stream.id}`}
            priority={priority}
            displayName={displayName}
            onPriority={setPriority}
            onDisplayName={setDisplayName}
          />
          <DialogFooter>
            <Button
              type="submit"
              disabled={parsedPriority === null || !changed || update.isPending}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Deletes a stream after a confirmation (`.delete`). */
function DeleteStreamDialog({
  stream,
  onOpenChange,
}: {
  stream: Stream;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const invalidate = useInvalidateStreams();
  const remove = useMutation(
    trpc.liveBreaks.sponsoredStreams.delete.mutationOptions({
      onSuccess: () => {
        toast.success(`${stream.twitchLogin} deleted.`);
        invalidate();
        onOpenChange(false);
      },
    }),
  );
  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Delete ${stream.twitchLogin}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            It leaves the widget now, and a live break's note about Priority names the channel by
            its Twitch name again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate({ id: stream.id })}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
